import { Disclosure } from "@headlessui/react";
import { ClipboardDocumentListIcon } from "@heroicons/react/24/solid";
import { ChevronRight } from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import AddButton from "../../../../components/AddButton";
import CloseIconButton from "../../../../components/CloseIconButton";
import EditOutlineButton from "../../../../components/EditOutlineButton";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../PermissionsConstants";
import { scorecardAddState, scorecardEditState } from "../atoms/atoms";
import { useGetScorecardByCaseNumber } from "../hooks/useGetScorecardByCaseNumber";

interface scorecardDetailsProps {
  rowData: any;
  rowIsExpanded: boolean;
  handleEdit: any;
  onClickCancelShow;
  setIsAdd;
  setReset;
}

export const ScoreCardRowDetails: React.FC<scorecardDetailsProps> = ({
  rowData,
  rowIsExpanded,
  setReset,
  handleEdit,
  onClickCancelShow,
  setIsAdd,
}) => {
  const [scorecardDetails, setScorecardDetails] = useState<any>([]);
  const {
    data: scorecardData,
    isFetching,
    isSuccess,
  } = useGetScorecardByCaseNumber(rowData.caseNumber);

  useEffect(() => {
    if (isSuccess && scorecardData) {
      setScorecardDetails(scorecardData);
    }
  }, [isSuccess, scorecardData]);
  const setAddState = useSetRecoilState(scorecardAddState);
  const setEditState = useSetRecoilState(scorecardEditState);
  const userPermissions = useRecoilValue(permissionsSelector);
  const viewPermission = PERMISSIONS.permission_scorecard_view;
  const addPermission = PERMISSIONS.permission_scorecard_add;
  const editPermission = PERMISSIONS.permission_scorecard_edit;

  return (
    <>
      {!isFetching && scorecardData && scorecardDetails && (
        <>
          <div className="flex justify-between pb-1 border-secondary border-b-2">
            <div className="flex gap-2 items-center">
              <div>
                <ClipboardDocumentListIcon className="text-white bg-blue-500 rounded-md h-7 w-7 p-1" />
              </div>
              <div className="flex flex-col leading tight">
                <h3 className="text-xs font-medium text-gray-500">Scorecard</h3>
                <h1 className="mr-auto font-bold text-primary text-md uppercase">
                  {scorecardDetails.account}
                </h1>
              </div>
            </div>
            <div className="flex justify-between">
              <div className="flex items-center gap-2">
                {userPermissions.includes(addPermission) ? (
                  <AddButton
                    className={"opacity-100"}
                    disabled={false}
                    buttonText="Add"
                    onClick={() => {
                      setIsAdd(true);
                      setAddState(true);
                    }}
                  />
                ) : (
                  ""
                )}
                {userPermissions.includes(editPermission) ? (
                  <EditOutlineButton
                    buttonText="Edit"
                    className={""}
                    disabled={false}
                    onClick={() => {
                      handleEdit(scorecardDetails);
                      setEditState(true);
                    }}
                  />
                ) : (
                  ""
                )}
              </div>
              <CloseIconButton onClick={() => onClickCancelShow()} />
            </div>
          </div>

          <div className="bg-bgDB">
            {rowIsExpanded ? (
              <div className="mt-4 mb-2 bg-white rounded-lg box-shadow">
                <div className="text-quinary font-bold font-sans py-2 px-6">
                  Case Detail
                </div>
                <div className="">
                  <div className="grid grid-cols-1 sm:grid-cols-2">
                    <div className="p-2 divide-y">
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Scorecard Number:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          <span className="pr-2">
                            {String(scorecardDetails.caseNumber)?.padStart(
                              10,
                              "0"
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Contact Name:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {scorecardDetails.contact}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Office Name:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {scorecardDetails.account}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          {" "}
                          Type:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {scorecardDetails.type}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          General Group:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {scorecardDetails.generalGroupC}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Descriptive Group:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {scorecardDetails.descriptiveGroupC}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Scorecard Area:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {scorecardDetails.scorecardAreaC}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Exceptions:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {scorecardDetails.exceptionsC}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Scorecard Area (Other):
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {scorecardDetails.scorecardAreaOtherC}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Policy#:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {scorecardDetails.policyC}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Customer#:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {scorecardDetails.customerC}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Amount:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {scorecardDetails.amountC
                            ? "$ " + scorecardDetails.amountC?.toFixed(2)
                            : ""}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Employee Status:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {scorecardDetails.employeeStatusC}
                        </div>
                      </div>
                    </div>
                    <div className="p-2 px-6 divide-y">
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Case Owner:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {scorecardDetails.createdBy}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Status:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {scorecardDetails.status}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Suspend To:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {scorecardDetails.suspendToC
                            ? new Date(
                                scorecardDetails.suspendToC
                              )?.toLocaleDateString()
                            : ""}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Close Reason:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {scorecardDetails.closeReasonC}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Write Off Amount:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {scorecardDetails.writeOffAmountC
                            ? "$ " +
                              scorecardDetails.writeOffAmountC?.toFixed(2)
                            : ""}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Escalated:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {scorecardDetails.isEscalated ? "True" : "False"}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Contact Email:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {scorecardDetails.contactEmail}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Manager:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {scorecardDetails.rslC}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Manager Email:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {scorecardDetails?.rslEmailC}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Agent Unknown:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {scorecardDetails?.agentUnknownC ? "True" : "False"}
                        </div>
                      </div>
                      {scorecardDetails?.status
                        ?.toLowerCase()
                        .includes("closed") ? (
                        <>
                          <div className="grid grid-cols-3 p-2">
                            <label className="col-span-1 mx-4 expandedLabel fontExpand">
                              Full Pay:
                            </label>
                            <div className="col-span-2 expandedData fontExpand">
                              {scorecardDetails?.fullPayC ? "Yes" : "No"}
                            </div>
                          </div>
                          <div className="grid grid-cols-3 p-2">
                            <label className="col-span-1 mx-4 expandedLabel fontExpand">
                              Dispute:
                            </label>
                            <div className="col-span-2 expandedData fontExpand">
                              {scorecardDetails?.disputeC}
                            </div>
                          </div>
                        </>
                      ) : (
                        ""
                      )}
                      <div className="grid grid-cols-3 p-2">
                        <label className="col-span-1 mx-4 expandedLabel fontExpand">
                          Is Active:
                        </label>
                        <div className="col-span-2 expandedData fontExpand">
                          {scorecardDetails?.isActive ? "True" : "False"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              ""
            )}
            <div className="flex flex-cols-2 w-full gap-2">
              {rowIsExpanded ? (
                <Disclosure defaultOpen>
                  {({ open }) => (
                    <>
                      <div className="w-1/2 bg-white rounded-lg box-shadow">
                        <Disclosure.Button className="disclosureBtn ml-2">
                          <ChevronRight
                            className={`${
                              open ? "transform rotate-90" : " text-gray-500"
                            } text-gray-500 w-5 h-5 `}
                          />
                          <span className="disclosureLabel ml-2">
                            Description Information
                          </span>
                        </Disclosure.Button>

                        <Disclosure.Panel className="text-xxs">
                          <div className="grid grid-cols-1 md:grid-cols-1">
                            <div className="p-2 divide-y">
                              <div className="grid grid-cols-3 p-2">
                                <label className="col-span-1 mx-4 expandedLabel fontExpand">
                                  Subject:
                                </label>
                                <label className="col-span-2 expandedData fontExpand">
                                  <span className="">
                                    {scorecardDetails.subject}
                                  </span>
                                </label>
                              </div>
                              <div className="grid grid-cols-3 p-2">
                                <label className="col-span-1 mx-4 expandedLabel fontExpand">
                                  Action Required OR Notification:
                                </label>
                                <div className="col-span-2 expandedData fontExpand">
                                  {
                                    scorecardDetails.actionRequiredOrNotificationC
                                  }
                                </div>
                              </div>
                              <div className="grid grid-cols-3 p-2">
                                <label className="col-span-1 mx-4 expandedLabel fontExpand">
                                  Action To Take:
                                </label>
                                <div className="col-span-2 expandedData fontExpand">
                                  {scorecardDetails.actionToTakeC}
                                </div>
                              </div>
                              {/* <div className="grid grid-cols-3 p-2">
                                <label className="col-span-1 mx-4 expandedLabel fontExpand">
                                  Additional Notes:
                                </label>
                                <div className="col-span-2 expandedData fontExpand">
                                  {scorecardDetails.managerEffectiveDate
                                    ? new Date(
                                        scorecardDetails.managerEffectiveDate
                                      )?.toLocaleDateString()
                                 : ""}
                                </div>
                              </div> */}
                            </div>
                          </div>
                        </Disclosure.Panel>
                      </div>
                    </>
                  )}
                </Disclosure>
              ) : (
                ""
              )}
              {rowIsExpanded ? (
                <Disclosure defaultOpen>
                  {({ open }) => (
                    <>
                      <div className="w-1/2 bg-white rounded-lg box-shadow">
                        <Disclosure.Button className="disclosureBtn ml-2">
                          <ChevronRight
                            className={`${
                              open ? "transform rotate-90" : " text-gray-500"
                            } text-gray-500 w-5 h-5 `}
                          />
                          <span className="disclosureLabel ml-2">
                            System Information
                          </span>
                        </Disclosure.Button>

                        <Disclosure.Panel className="text-xxs ">
                          <div className="grid grid-cols-1 md:grid-cols-1 mx-2">
                            <div className="p-2 divide-y">
                              <div className="grid grid-cols-4 p-2">
                                <label className="col-span-1 mx-4 expandedLabel fontExpand">
                                  Date/Time Opened:
                                </label>
                                <label className="col-span-2 expandedData fontExpand">
                                  <span className="">
                                    {scorecardDetails.createdDate
                                      ? new Date(
                                          scorecardDetails.createdDate
                                        )?.toLocaleDateString() +
                                        " " +
                                        new Date(
                                          scorecardDetails.createdDate
                                        ).toLocaleTimeString(
                                          navigator.language,
                                          {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          }
                                        )
                                      : ""}
                                  </span>
                                </label>
                              </div>
                              <div className="grid grid-cols-4 p-2">
                                <label className="col-span-1 mx-4 expandedLabel fontExpand">
                                  Created By:
                                </label>
                                <div className="expandedData fontExpand">
                                  {scorecardDetails.createdBy}
                                </div>
                                <label className="col-span-1 mx-4 expandedLabel fontExpand">
                                  Created Date:
                                </label>
                                <div className="expandedData fontExpand">
                                  {scorecardDetails.createdDate
                                    ? new Date(
                                        scorecardDetails.createdDate
                                      )?.toLocaleDateString()
                                    : ""}
                                </div>
                              </div>
                              <div className="grid grid-cols-4 p-2">
                                <label className="col-span-1 mx-4 expandedLabel fontExpand">
                                  Date/Time Closed:
                                </label>
                                <div className="col-span-2 expandedData fontExpand">
                                  {scorecardDetails.closedDate
                                    ? new Date(
                                        scorecardDetails.closedDate
                                      )?.toLocaleDateString() +
                                      " " +
                                      new Date(
                                        scorecardDetails.closedDate
                                      ).toLocaleTimeString(navigator.language, {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })
                                    : ""}
                                </div>
                              </div>
                              <div className="grid grid-cols-4 p-2">
                                <label className="col-span-1 mx-4 expandedLabel fontExpand">
                                  Last Modified By:
                                </label>
                                <div className="expandedData fontExpand">
                                  {scorecardDetails.lastModifiedBy}{" "}
                                </div>
                                <label className="col-span-1 mx-4 expandedLabel fontExpand">
                                  Modified Date:
                                </label>
                                <div className="expandedData fontExpand">
                                  {scorecardDetails.modifiedDate
                                    ? new Date(
                                        scorecardDetails.modifiedDate
                                      )?.toLocaleDateString()
                                    : ""}
                                </div>
                              </div>
                            </div>
                          </div>
                        </Disclosure.Panel>
                      </div>
                    </>
                  )}
                </Disclosure>
              ) : (
                ""
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};
